import React from "react";
import { Card, CardContent } from "../ui/CCard";
import { Label } from "../ui/Label";
import { Button } from "../ui/Button";
import { Clock, X, Activity, Brain } from "lucide-react";

const SHOW_AI_FEEDBACK = false; // "What Went Well" AI feedback hidden for now (2026-10-24); data is still stored

export default function HistoryModal({ appt, onClose }) {
  if (!appt) return null;
  
  const isGP = appt.consultation_type === "general_medicine";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col animate-fade-in-up">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-2">
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold flex items-center gap-1 ${
                isGP ? "bg-teal-100 text-teal-800" : "bg-purple-100 text-purple-800"
              }`}>
                {isGP ? <Activity className="w-3.5 h-3.5" /> : <Brain className="w-3.5 h-3.5" />}
                {isGP ? "General Medicine" : "Mental Health"}
              </span>
              <h3 className="text-xl font-bold text-gray-900">
                Consultation Note
              </h3>
            </div>
            <p className="text-sm text-gray-500 flex items-center mt-1">
              <Clock className="w-4 h-4 mr-1" /> {appt.sessiondate || "No date"} {appt.sessiontime ? `at ${appt.sessiontime}` : ""}
              {appt.sessionduration ? ` • ${appt.sessionduration} mins` : ""}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Scrollable Content */}
        <div className="p-6 overflow-y-auto flex-1 bg-gray-50/50">
          <div className="space-y-6">
            
            {isGP ? (
              // GENERAL MEDICINE HISTORY VIEW
              <>
                <Card className="border-gray-200">
                  <CardContent className="p-5">
                    <h4 className="font-semibold text-primary-800 mb-4 border-b pb-2">1. Chief Complaints & Examination</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <Label className="text-gray-500 font-medium">Chief Complaints & Symptoms</Label>
                        <p className="mt-1 text-gray-900 bg-white p-3 rounded-lg border border-gray-100 min-h-[60px] whitespace-pre-wrap">{appt.gp_symptoms || "N/A"}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500 font-medium">Vitals & Physical Examination</Label>
                        <p className="mt-1 text-gray-900 bg-white p-3 rounded-lg border border-gray-100 min-h-[60px] whitespace-pre-wrap">{appt.gp_vitals || "N/A"}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-gray-200">
                  <CardContent className="p-5">
                    <h4 className="font-semibold text-primary-800 mb-4 border-b pb-2">2. Assessment & Diagnosis</h4>
                    <div className="text-sm">
                      <Label className="text-gray-500 font-medium">Diagnosis</Label>
                      <p className="mt-1 text-gray-900 bg-white p-3 rounded-lg border border-gray-100 min-h-[60px] whitespace-pre-wrap">{appt.gp_diagnosis || "N/A"}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-gray-200">
                  <CardContent className="p-5">
                    <h4 className="font-semibold text-primary-800 mb-4 border-b pb-2">3. Prescription & Advice</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="font-serif italic font-black text-rose-600 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded text-xs leading-none">℞</span>
                          <Label className="text-gray-700 font-semibold">Prescription / Medications</Label>
                        </div>
                        <p className="mt-1 text-gray-900 bg-white p-3 rounded-lg border border-gray-100 min-h-[60px] whitespace-pre-wrap font-mono text-xs">{appt.gp_prescription || "N/A"}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500 font-medium">General Advice & Follow Up</Label>
                        <p className="mt-1 text-gray-900 bg-white p-3 rounded-lg border border-gray-100 min-h-[60px] whitespace-pre-wrap">{appt.gp_advice || "N/A"}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              // MENTAL HEALTH HISTORY VIEW
              <>
                <Card className="border-gray-200">
                  <CardContent className="p-5">
                    <h4 className="font-semibold text-primary-800 mb-4 border-b pb-2">Clinical Observations & Presentation</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <Label className="text-gray-500 font-medium">Mood & Affect</Label>
                        <p className="mt-1 text-gray-900 bg-white p-3 rounded-lg border border-gray-100">{appt.mood || "N/A"}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500 font-medium">Main Themes Discussed</Label>
                        <p className="mt-1 text-gray-900 bg-white p-3 rounded-lg border border-gray-100">{appt.mainthemes || "N/A"}</p>
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-gray-500 font-medium">Resistance or Engagement</Label>
                        <p className="mt-1 text-gray-900 bg-white p-3 rounded-lg border border-gray-100">{appt.resistanceorengagement || "N/A"}</p>
                      </div>
                      {SHOW_AI_FEEDBACK && appt.what_went_well && (
                        <div className="md:col-span-2">
                          <Label className="text-indigo-600 font-medium">What Went Well (AI Analysis)</Label>
                          <p className="mt-1 text-indigo-950 bg-indigo-50/50 p-3 rounded-lg border border-indigo-100">{appt.what_went_well}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-gray-200">
                  <CardContent className="p-5">
                    <h4 className="font-semibold text-primary-800 mb-4 border-b pb-2">Interventions & Techniques</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <Label className="text-gray-500 font-medium">Interventions Used</Label>
                        <p className="mt-1 text-gray-900 bg-white p-3 rounded-lg border border-gray-100">{appt.interventions || "N/A"}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500 font-medium">Interventions Effectiveness</Label>
                        <p className="mt-1 text-gray-900 bg-white p-3 rounded-lg border border-gray-100">{appt.interventionseffectiveness || "N/A"}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-gray-200">
                  <CardContent className="p-5">
                    <h4 className="font-semibold text-primary-800 mb-4 border-b pb-2">Working Formulation & Plan</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <Label className="text-gray-500 font-medium">Updated Hypothesis</Label>
                        <p className="mt-1 text-gray-900 bg-white p-3 rounded-lg border border-gray-100">{appt.updatedhypothesis || "N/A"}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500 font-medium">Session Outcome</Label>
                        <p className="mt-1 text-gray-900 bg-white p-3 rounded-lg border border-gray-100">{appt.sessionoutcome || "N/A"}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500 font-medium">Next Plan</Label>
                        <p className="mt-1 text-gray-900 bg-white p-3 rounded-lg border border-gray-100">{appt.nextplan || "N/A"}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-gray-200">
                  <CardContent className="p-5">
                    <h4 className="font-semibold text-primary-800 mb-4 border-b pb-2">Personal Reflection</h4>
                    <div className="text-sm">
                      <Label className="text-gray-500 font-medium">Self-Reflection</Label>
                      <p className="mt-1 text-gray-900 bg-white p-3 rounded-lg border border-gray-100">{appt.selfreflection || "N/A"}</p>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

          </div>
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-white text-right rounded-b-2xl">
          <Button onClick={onClose} variant="secondary">Close</Button>
        </div>
      </div>
    </div>
  );
}
